import { useCallback, useState, useContext } from 'react';
import { FirebaseAppContext } from 'libs.firebase_react';
import { JobTypes } from 'libs.jobs.common';
import { useCreateJob } from 'libs.jobs.react';
import { useLocalizationImportV2 } from 'libs.localization.react';
import { useImportState } from '../contexts/import_state.context';
import { useImportFromAsset } from './use_import_from_asset';
export const useImportSubmission = (params) => {
    const { documentType, assetId } = params;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { importLocalization } = useLocalizationImportV2({ onImport: () => { } });
    const createJob = useCreateJob();
    const { logEvent } = useContext(FirebaseAppContext);
    const { mutate: importFromAsset } = useImportFromAsset();
    const { selectedEligibleLocales, sourceLocalizationId, targetLocalizationId, files, localeMap } = useImportState();
    const handleSubmit = useCallback(async () => {
        if (isSubmitting || !documentType) {
            return;
        }
        try {
            setIsSubmitting(true);
            switch (documentType) {
                case 'MEDABLE_FORMAT': {
                    if (!files || Object.keys(files).length === 0) {
                        console.error('Files are required for MEDABLE_FORMAT');
                        return;
                    }
                    const filesToImport = Object.values(files)
                        .filter((file) => file.status === 'success' && file.firebaseUrl)
                        .map((file) => file.firebaseUrl);
                    await importLocalization({ files: filesToImport, requiredIds: [], supportedLocales: [] }, { onSuccess: () => setIsSubmitting(false) });
                    break;
                }
                case 'NON_MEDABLE_FORMAT': {
                    if (!files ||
                        Object.keys(files).length === 0 ||
                        !localeMap ||
                        Object.keys(localeMap).length === 0 ||
                        !assetId) {
                        console.error('Files, localeMap, and assetId are required for NON_MEDABLE_FORMAT');
                        return;
                    }
                    // Log the import event
                    logEvent('import_with_ai_submit', {
                        assetId,
                        locales: Object.keys(localeMap).join(', '),
                    });
                    // Create instant migration jobs for each file
                    for (const [id, file] of Object.entries(files)) {
                        const locale = localeMap[id];
                        if (locale && file.firebaseUrl && file.status === 'success') {
                            createJob(JobTypes.TRANSLATION__INSTANT_MIGRATION, {
                                asset_id: assetId,
                                locale: locale.value,
                                document_url: file.firebaseUrl,
                                document_filename: file.file.name,
                            });
                        }
                    }
                    break;
                }
                case 'MEDABLE_FORMAT_EXISTING_CLONE':
                case 'MEDABLE_FORMAT_EXISTING_VERSION': {
                    if (!selectedEligibleLocales) {
                        console.error('Selected eligible locales are required for MEDABLE_FORMAT_EXISTING_CLONE/VERSION');
                        return;
                    }
                    const items = selectedEligibleLocales.map((item) => ({
                        locale: item.locale,
                        version: item.version,
                    }));
                    importFromAsset({ sourceAssetId: sourceLocalizationId, targetAssetId: targetLocalizationId, items });
                    break;
                }
                default:
                    console.warn(`Unhandled document type: ${documentType}`);
                    break;
            }
        }
        catch (error) {
            console.error('Import failed:', error);
        }
        finally {
            setIsSubmitting(false);
        }
    }, [
        documentType,
        assetId,
        isSubmitting,
        importLocalization,
        createJob,
        logEvent,
        importFromAsset,
        selectedEligibleLocales,
        sourceLocalizationId,
        targetLocalizationId,
        files,
        localeMap,
    ]);
    return { isSubmitting, handleSubmit };
};
